import { DateTime, Duration } from 'luxon'
import { NextPage } from 'next'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { publisher } from '@/constants/seo'
import { slugs } from '@/constants/slugs'
import { ProfileContextProvider } from '@/contexts/ProfileContext'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { Seo } from '@/layout/Seo'
import { VideoObjectJsonLd } from '@/layout/Seo/JsonLd'
import { getMediaMetaData } from '@/layout/Seo/SeoHelper'
import { GetAppBannerProvider } from '@/organisms/GetAppBanner'
import { getLivestreamCta, LivestreamCta } from '@/services/CtaService'
import { createLivestreamDeepLink } from '@/services/DeepLinkService'
import { getLivestreamMetaData, LivestreamMeta } from '@/services/LivestreamService'
import { ProjectMeta } from '@/services/ProjectsService'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { shouldHideProjectFromSearchEngines } from '@/utils/seo/searchEngineHelper'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { LivestreamView } from '@/views/LivestreamView'

interface LivestreamPageProps {
  deepLink: string | null
  locale: string
  livestreamMeta: LivestreamMeta
  livestreamCta: LivestreamCta
  projectMeta: ProjectMeta
  streamId: string
}

export const LivestreamPage: NextPage<LivestreamPageProps> = (props) => {
  const { t } = useTranslate('livestream')
  const { livestreamMeta, projectMeta, livestreamCta } = props
  const basePath = getBasePath(props.locale)
  const url = `${basePath}${paths.livestream.index}/${props.streamId}`
  const meta = getMediaMetaData({
    cloudinaryImagePath: livestreamMeta.bannerCloudinaryPath,
    description: livestreamMeta.description,
    title: t('livestreamTitle', `{{projectName}} Livestream: {{livestreamName}}`, {
      projectName: props.projectMeta.name,
      livestreamName: livestreamMeta.name,
    }),
    url,
  })
  const startDate = DateTime.fromISO(livestreamMeta.startAt)
  const endDate = livestreamMeta.endedAt ? DateTime.fromISO(livestreamMeta.endedAt) : startDate.plus({ hours: 1 }) // Add a default of 1 hour if endedAt is not specified
  const duration = livestreamMeta?.durationSeconds
    ? Duration.fromDurationLike({ hour: 0, minute: 0, second: livestreamMeta.durationSeconds }).normalize().toISO()
    : endDate.diff(startDate).shiftTo('hours', 'minutes', 'seconds').toISO()
  const shouldHide = shouldHideProjectFromSearchEngines(props.projectMeta)

  return (
    <>
      <Seo {...meta} nofollow={shouldHide} noindex={shouldHide} path={`${paths.livestream.index}/${props.streamId}`} />
      <VideoObjectJsonLd
        url={url}
        name={livestreamMeta.name}
        description={livestreamMeta.description}
        duration={duration}
        thumbnailUrl={`https://images.angelstudios.com/image/upload/b_rgb:000000,c_fill,h_630,w_1200,g_north/${livestreamMeta?.bannerCloudinaryPath}`}
        contentUrl={livestreamMeta.url}
        uploadDate={startDate.toISO()}
        expires={endDate.toISO()}
        publication={{
          type: 'BroadcastEvent',
          isLiveBroadcast: true,
          startDate: startDate.toISO(),
          endDate: endDate.toISO(),
        }}
        publisher={publisher}
      />
      <ProfileContextProvider>
        <ProjectMetaProvider project={projectMeta}>
          <GetAppBannerProvider deepLink={props.deepLink} projectSlug={projectMeta.slug} context="livestream">
            <LivestreamView
              livestreamCta={livestreamCta}
              livestreamMeta={livestreamMeta}
              projectMeta={projectMeta}
              streamId={props.streamId}
            />
          </GetAppBannerProvider>
        </ProjectMetaProvider>
      </ProfileContextProvider>
    </>
  )
}

const getParamsFromPath = (id: string) => {
  if (isNaN(id as unknown as number)) {
    return {
      guid: id,
    }
  } else {
    return {
      id,
    }
  }
}

export const getStaticProps = getStaticPropsErrorHandler<LivestreamPageProps>(
  { filePath: 'pages/[locale]/livestreams/[id]/index.tsx' },
  async ({ federationClient, locale, next404, params, preview }) => {
    const id = getItemOrFirstEntry(params.id)
    if (!id) return next404(Durations.FIFTEEN_MINUTES_IN_SECONDS)

    // TODO - The getParamsFromPath function is messed up. It uses type casting as if it was changing the value.
    // I tried fixing it, but things broke downstream. The downstream components likely make
    // assumptions about these values in their broken state. Need a deeper dive to fix this sometime.
    const { id: livestreamId, guid } = getParamsFromPath(id)

    const opts = { locale, preview }
    const livestream = await getLivestreamMetaData({ id: livestreamId, guid }, federationClient, opts)
    if (!livestream) return next404(Durations.FIFTEEN_MINUTES_IN_SECONDS)

    const { livestreamMeta, projectMeta } = livestream

    if (livestreamMeta.projectSlug === slugs.theChosen) {
      return {
        redirect: {
          destination: paths.watch.index,
          permanent: false,
        },
      }
    }

    // Return 404 if the livestream becomes archived
    if (livestreamMeta.isArchived) return next404(Durations.ONE_HOUR_IN_SECONDS)

    const shouldRedirectToGuid =
      typeof livestreamId !== 'undefined' &&
      !isNaN(livestreamId as unknown as number) &&
      parseInt(livestreamId as string) < 550

    if (shouldRedirectToGuid) {
      return {
        redirect: {
          destination: `/livestreams/${livestreamMeta?.guid}`,
          permanent: true,
        },
      }
    }

    const path = `${paths.livestream.index}/${livestreamMeta?.guid}`

    const [livestreamCta, deepLink, translations] = await Promise.all([
      getLivestreamCta(livestreamMeta.projectSlug, livestreamMeta.guid, { locale, preview }),
      createLivestreamDeepLink(
        {
          path,
          guid: livestreamMeta?.guid,
          projectSlug: projectMeta?.slug,
          projectName: projectMeta?.name,
          contentName: livestreamMeta?.name as string,
          audioLocale: locale,
          captionLocale: locale,
        },
        {
          campaign: 'force-app-install',
          feature: 'app-prompt',
          channel: 'angel-web',
        },
      ),
      loadTranslations(locale, [
        'common',
        'ads',
        'app-promo',
        'livestream',
        'chatbot',
        'account',
        'guild-benefits',
        'guild',
      ]),
    ])

    return {
      props: {
        deepLink,
        locale,
        livestreamMeta,
        livestreamCta,
        projectMeta,
        streamId: id,
        ...translations,
      },
      revalidate: livestreamMeta.endedAt ? false : Durations.FIVE_MINUTES_IN_SECONDS,
    }
  },
)

export const getStaticPaths = noPreRenderedStaticPaths

export default LivestreamPage
